import type { ReactElement } from 'react';

import { parseDocStyle } from './../ricos-content/lib/convertDocStyleToDraft';
import type { Decoration, DocumentStyle as RichContentDocumentStyle } from './../ricos-schema/legacy/wix/rich_content/v1';
import { Decoration_Type, FontSizeData_fontType, Node_Type } from './../ricos-schema/legacy/wix/rich_content/v1';
import type {
  EventSource,
  PolicySubscriber,
  PublisherProvider,
  RichTextNode,
  RicosTheme,
  SubscriptorProvider,
} from './../ricos-types';

import { RicosDocumentStyle } from './document-style/document-style';
import { TextNodeTransformer } from './text-node-transformer';
import TextualTheme from './textual-theme/textual-theme';

type Topics = [
  'ricos.styles.functionality.themeUpdate',
  'ricos.styles.functionality.documentStyleUpdate'
];

const TOPICS: Topics = [
  'ricos.styles.functionality.themeUpdate',
  'ricos.styles.functionality.documentStyleUpdate',
];

export class RicosStyles implements PolicySubscriber<Topics>, EventSource<Topics> {
  private theme: TextualTheme = new TextualTheme({});

  private documentStyle: RicosDocumentStyle = new RicosDocumentStyle({});

  id = 'ricos-styles';

  topicsToPublish = TOPICS;

  private editorElement: HTMLElement | null = null;

  topicsToSubscribe = TOPICS;

  publishers!: PublisherProvider<Topics>;

  subscriptors!: SubscriptorProvider<Topics>;

  setEditorElement = (e: HTMLElement) => {
    this.editorElement = e;
  };

  toStyleTags(): ReactElement[] {
    return [this.theme.toStyleTag(), this.documentStyle.toStyleTag()];
  }

  onThemeUpdate = (callback: (theme: RicosTheme) => void) =>
    this.subscriptors
      ?.byTopic('ricos.styles.functionality.themeUpdate')
      .subscribe((_, data) => callback(data));

  onDocumentStyleUpdate = (callback: (documentStyle: RichContentDocumentStyle) => void) =>
    this.subscriptors
      ?.byTopic('ricos.styles.functionality.documentStyleUpdate')
      .subscribe((_, data) => callback(data));

  getDecoration(
    node: RichTextNode | undefined,
    decorationType: Decoration_Type,
    nodeDom?: HTMLElement
  ): Decoration {
    if (
      !node ||
      ![
        Node_Type.PARAGRAPH,
        Node_Type.HEADING,
        Node_Type.BLOCKQUOTE,
        Node_Type.CODE_BLOCK,
      ].includes(node?.type)
    ) {
      // Wrong node type
      return {} as Decoration;
    }
    const element = nodeDom || this.editorElement;
    if (element) {
      const styles = window.getComputedStyle(element);
      const fontSize = styles.getPropertyValue(
        `--ricos-custom-${propertyNameKeyFromNodeType(node)}-font-size`
      );
      return {
        type: Decoration_Type.FONT_SIZE,
        fontSizeData: {
          unit: FontSizeData_fontType.PX,
          value: parseInt(fontSize, 10),
        },
      };
    }

    const nodeType = new TextNodeTransformer(node).getDocumentStyleKey();
    const documentStyleDecoration = this.documentStyle.getDecoration(nodeType, decorationType);
    const themeDecoration = this.theme.getDecoration(nodeType, decorationType);
    return themeDecoration.overrideWith(documentStyleDecoration).getDecoration();
  }

  getComputedLineHeight(node: RichTextNode | undefined, nodeDom?: HTMLElement): string | undefined {
    if (!node) {
      return undefined;
    }

    if (
      ![
        Node_Type.PARAGRAPH,
        Node_Type.HEADING,
        Node_Type.BLOCKQUOTE,
        Node_Type.CODE_BLOCK,
      ].includes(node?.type)
    ) {
      return undefined;
    }

    const element = nodeDom || this.editorElement;

    if (element) {
      const styles = window.getComputedStyle(element);
      const { lineHeight, fontSize } = styles;

      return (parseFloat(lineHeight) / parseFloat(fontSize)).toFixed(1);
    }
  }

  getTextStyle(node: RichTextNode) {
    const nodeType = new TextNodeTransformer(node).getDocumentStyleKey();
    const documentStyleTextStyle = this.documentStyle.getTextStyle(nodeType);
    const themeTextStyle = this.theme.getTextStyle(nodeType);
    return themeTextStyle.overrideWith(documentStyleTextStyle.getTextStyle()).getTextStyle();
  }

  getNodeStyle(node: RichTextNode) {
    const nodeType = new TextNodeTransformer(node).getDocumentStyleKey();
    const documentStyleNodeStyle = this.documentStyle.getNodeStyle(nodeType);
    const themeTextStyle = this.theme.getNodeStyle(nodeType);
    return themeTextStyle.overrideWith(documentStyleNodeStyle.getNodeStyle()).getNodeStyle();
  }

  getTheme() {
    return this.theme;
  }

  setTheme(theme: RicosTheme) {
    this.theme = new TextualTheme(theme);
    this.publishers?.byTopic('ricos.styles.functionality.themeUpdate').publish(theme);
    return this;
  }

  getDocumentStyle() {
    return this.documentStyle;
  }

  setDocumentStyle(documentStyle: RichContentDocumentStyle) {
    this.documentStyle = new RicosDocumentStyle(documentStyle);
    this.publishers
      ?.byTopic('ricos.styles.functionality.documentStyleUpdate')
      .publish(documentStyle);
    return this;
  }

  toDraftDocumentStyle() {
    return parseDocStyle(this.documentStyle.toContent());
  }
}

const propertyNameKeyFromNodeType = (node: RichTextNode): string => {
  switch (node.type) {
    case Node_Type.HEADING:
      return `h${node.headingData.level}`;
    case Node_Type.BLOCKQUOTE:
      return 'quote';
    case Node_Type.CODE_BLOCK:
      return 'code-block';
    default:
      return 'p';
  }
};
